import { OrderV2 } from '@kotipizzagroup/kotipizza-orders-api-client';
import { StyledPaper } from '../../Styled/Styled';
import { CustomerDetailsContainer, FieldGrid, FieldLabel, FieldValue, InformationText } from './Styled';
import styled from 'styled-components';
import { SvgIconComponent } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import { DELIVERY_TYPES } from '../constants';
import { formatOrderMoment } from '../utils/orderRowFields';
import { useTranslation } from 'react-i18next';
import { getOrderPaymentState, notPaidTimeStamp, OrderPaymentState } from '../../utils/orderUtils';
import { useAllRestaurants } from '../../../apis/shops-api';

// Keyed on the enum rather than a chain of `&&`, so adding a payment state fails to compile here
// instead of silently rendering an empty value.
const PAYMENT_STATE_DISPLAY: Record<
  OrderPaymentState,
  { Icon: SvgIconComponent; color: 'success' | 'warning' | 'error'; labelKey: string }
> = {
  [OrderPaymentState.Paid]: { Icon: CheckCircleIcon, color: 'success', labelKey: 'Paid' },
  [OrderPaymentState.PaidInRestaurant]: { Icon: CheckCircleIcon, color: 'success', labelKey: 'Pay in restaurant' },
  [OrderPaymentState.Unpaid]: { Icon: WarningIcon, color: 'warning', labelKey: 'Unpaid' },
  [OrderPaymentState.PaymentNotCompleted]: { Icon: CancelIcon, color: 'error', labelKey: 'Payment failed' },
  [OrderPaymentState.PaymentReceivedTooLate]: {
    Icon: CancelIcon,
    color: 'error',
    labelKey: 'Payment received too late',
  },
  [OrderPaymentState.PaymentCancelled]: { Icon: CancelIcon, color: 'error', labelKey: 'Payment cancelled' },
};

/**
 * The order's own facts, as the first card of the details view: who ordered it, where it is going,
 * and how it was paid for. One card among eight — `OrderDetailsView` is what assembles them.
 */
export const OrderInfoCard: React.FC<{ order: OrderV2 }> = ({ order }) => {
  const { data: restaurants } = useAllRestaurants();
  const { t } = useTranslation();

  const restaurant = restaurants?.find((restaurant) => restaurant.shopId === order.ShopId);

  const orderPaymentState = getOrderPaymentState(order);
  const payment = PAYMENT_STATE_DISPLAY[orderPaymentState];
  const PaymentStateIcon = payment.Icon;

  // An unpaid order carries a placeholder timestamp rather than nothing, so it needs checking for.
  const paidAt = order.orderPaid && order.orderPaid !== notPaidTimeStamp ? order.orderPaid : null;

  return (
    <StyledPaper variant="outlined">
      <CustomerDetailsContainer>
        <InformationRow>
          <DetailLabel>{t('Order placed')}</DetailLabel>{' '}
          {order.orderPlaced ? formatOrderMoment(order.orderPlaced, t) : '-'}
        </InformationRow>
        <InformationRow>
          <DetailLabel>{t('Restaurant')}</DetailLabel> {order.ShopExternalId} - {restaurant?.displayName}
        </InformationRow>
        <InformationRow>
          <DetailLabel>{t('Customer')}</DetailLabel>
          <InformationText>
            <div>
              {order.deliveryContact?.firstName} {order.deliveryContact?.lastName}
            </div>
            <div>{order.deliveryContact?.email}</div>
            <div>{order.deliveryContact?.phoneNumber}</div>
            {order.SubscribedToLoyalty ? (
              <LoyaltyStatus $inLoyalty={true}>{t('Loyalty member')}</LoyaltyStatus>
            ) : (
              <LoyaltyStatus $inLoyalty={false}>{t('Not logged in as loyalty member')}</LoyaltyStatus>
            )}

            {order.CustomerId && order.CustomerId > 0 ? (
              <div> {t('customer.idLabel') + ':' + order.CustomerId}</div>
            ) : null}
          </InformationText>
        </InformationRow>
        {order.deliveryType === DELIVERY_TYPES.delivery && (
          <InformationRow>
            <DetailLabel>{t('Delivery address')}</DetailLabel> {order.deliveryContact?.city}{' '}
            {order.deliveryContact?.street} {order.deliveryContact?.apartmentNumber} {order.deliveryContact?.postalCode}
          </InformationRow>
        )}
        <InformationRow>
          <DetailLabel>{t('Additional information')}</DetailLabel>{' '}
          {order.deliveryContact?.doorCode || order.deliveryContact?.extraInfo || order.shippingInstruction ? (
            <>
              {order.deliveryContact?.doorCode} - {order.deliveryContact?.extraInfo}
              {order.shippingInstruction}
            </>
          ) : (
            <MissingText>{t('No additional information')}</MissingText>
          )}
        </InformationRow>
        <PaymentGroup>
          <PaymentGroupTitle>{t('orderDetails.paymentInformation')}</PaymentGroupTitle>
          <PaymentFieldGrid>
            <FieldLabel>{t('Payment status')}</FieldLabel>
            <PaymentValue>
              <PaymentStateIcon color={payment.color} />
              {t(payment.labelKey)}
            </PaymentValue>

            <FieldLabel>{t('Payment method')}</FieldLabel>
            <PaymentValue>
              {(order.shoppingCartPrice?.paymentRows?.length ?? 0) > 1
                ? t('Split payment')
                : order.paymentMethod === order.paymentOption
                ? `${order.paymentMethod}`
                : `${order.paymentMethod}, ${order.paymentOption}`}
            </PaymentValue>

            {/* No row at all when unpaid: the status above already reads "Ei maksettu". */}
            {paidAt && (
              <>
                <FieldLabel>{t('Paid')}</FieldLabel>
                <PaymentValue>{formatOrderMoment(paidAt, t)}</PaymentValue>
              </>
            )}
          </PaymentFieldGrid>
        </PaymentGroup>
      </CustomerDetailsContainer>
    </StyledPaper>
  );
};

// The label/value rows of this card carry the long values (address, tracking URL, email), so they
// stack on phones and become the two-column layout from sm up.
const InformationRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 0px 8px;
  min-width: 0;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    flex-direction: row;
    gap: 8px;
  }
`;

const DetailLabel = styled.div`
  font-weight: bold;
  overflow-wrap: anywhere;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    width: 18%;
    min-width: 8rem;
    flex-shrink: 0;
  }
`;

// Marks where the order's own facts end and how it was paid for begins.
const PaymentGroup = styled.div`
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.palette.divider};
`;

const PaymentGroupTitle = styled.div`
  margin-bottom: 8px;
  font-weight: bold;
`;

/**
 * Lines the payment labels up with the rows above, which are `DetailLabel`-wide, so every value in
 * the card starts at the same x. `minmax(8rem, 18%)` is that rule expressed as a track — a grid
 * cannot take `width` plus `min-width` — and the 8px column gap matches `InformationRow`'s, since a
 * different gap would offset the values by the difference.
 *
 * Only from sm up: below that the rows above stack and there is no column to line up with, and a
 * fixed 8rem label would just spend a phone's width on three short words.
 */
const PaymentFieldGrid = styled(FieldGrid)`
  ${({ theme }) => theme.breakpoints.up('sm')} {
    grid-template-columns: minmax(8rem, 18%) 1fr;
    column-gap: 8px;
  }
`;

/**
 * The status icon sits in the text flow rather than in a flex row. A flex container whose items are
 * not baseline-aligned has no baseline of its own, so the grid fell back to aligning this box's
 * bottom edge and the value rode up above its label. In normal flow the box's baseline is its first
 * line of text — the same thing the other rows align on.
 */
const PaymentValue = styled(FieldValue)`
  svg {
    margin-right: 4px;
    font-size: 1.125rem;
    vertical-align: text-bottom;
  }
`;

const LoyaltyStatus = styled.div<{ $inLoyalty: boolean }>`
  margin-top: 12px;
  color: ${({ theme, $inLoyalty }) => ($inLoyalty ? theme.palette.success.main : theme.palette.warning.main)};
`;

const MissingText = styled.div`
  color: ${({ theme }) => theme.palette.text.secondary};
`;
